import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Play, X } from "lucide-react";
import { films, otherCredits, images } from "../i18n";

const reveal = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
};

export const FictionSection = ({ t }) => {
  const [trailer, setTrailer] = useState(null);
  const featured = films[0];
  const rest = films.slice(1).filter((f) => f.id !== "toto");

  return (
    <section id="fiction" className="py-24 sm:py-32 lg:py-40">
      <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
        <motion.div {...reveal} className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end mb-16">
          <div className="lg:col-span-7">
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-brass mb-6">{t.fiction.overline}</p>
            <h2 data-testid="chapter-02-title" className="font-serif text-4xl sm:text-6xl tracking-tight leading-[1.05] text-ink">
              {t.fiction.title}
            </h2>
          </div>
          <p className="lg:col-span-5 text-base sm:text-lg text-ink2 leading-relaxed">
            {t.fiction.body}
          </p>
        </motion.div>

        <motion.a
          {...reveal}
          data-testid="film-card-quantic"
          href={featured.url}
          target="_blank"
          rel="noopener noreferrer"
          className="group relative block overflow-hidden rounded-sm mb-4 img-frame"
        >
          <img src={images.fiction} alt={t.fiction.featuredTitle} className="w-full h-[340px] sm:h-[460px] object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/35 to-black/10" />
          <div className="absolute bottom-0 inset-x-0 p-7 sm:p-10 flex items-end justify-between gap-6">
            <div>
              <span className="inline-block font-mono text-[11px] uppercase tracking-[0.25em] text-[#D4AF37] border border-[#D4AF37]/60 bg-black/50 backdrop-blur-sm rounded-full px-4 py-1.5 mb-4">
                {t.fiction.featuredTag}
              </span>
              <h3 className="font-serif text-4xl sm:text-6xl text-white tracking-tight">{t.fiction.featuredTitle}</h3>
              <p className="text-white/70 text-sm sm:text-base mt-2 max-w-md">{t.fiction.featuredBody}</p>
            </div>
            <span className="hidden sm:inline-flex items-center gap-2 text-white text-sm shrink-0 group-hover:text-[#D4AF37] transition-colors">
              {t.fiction.featuredLink}
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </span>
          </div>
        </motion.a>

        <div className="border-t border-line">
          {rest.map((f, i) => {
            const rowCls =
              "group flex items-center justify-between gap-6 py-6 sm:py-7 border-b border-line hover:bg-surface transition-colors duration-300 px-2 sm:px-4 -mx-2 sm:-mx-4 w-full text-left";
            const inner = (
              <>
                <div className="flex items-baseline gap-5 sm:gap-8 min-w-0">
                  <span className="font-mono text-xs text-ink3 shrink-0">{String(i + 2).padStart(2, "0")}</span>
                  <h3 className="font-serif text-2xl sm:text-4xl tracking-tight text-ink group-hover:text-brass transition-colors duration-300 group-hover:translate-x-1 truncate">
                    {f.title}
                  </h3>
                </div>
                <div className="flex items-center gap-4 sm:gap-6 shrink-0">
                  <span className="hidden sm:inline font-mono text-[11px] uppercase tracking-[0.2em] text-ink3">
                    {t.fiction.types[f.type]}
                  </span>
                  <span className="inline-flex items-center gap-1.5 text-sm text-ink2 group-hover:text-ink transition-colors">
                    {t.fiction.watch}
                    {f.youtube || f.video ? (
                      <Play size={14} className="transition-transform duration-300 group-hover:scale-110" />
                    ) : (
                      <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    )}
                  </span>
                </div>
              </>
            );
            return f.youtube || f.video ? (
              <motion.button
                key={f.id}
                {...reveal}
                transition={{ ...reveal.transition, delay: i * 0.04 }}
                data-testid={`film-card-${f.id}`}
                onClick={() => setTrailer(f)}
                className={rowCls}
              >
                {inner}
              </motion.button>
            ) : (
              <motion.a
                key={f.id}
                {...reveal}
                transition={{ ...reveal.transition, delay: i * 0.04 }}
                data-testid={`film-card-${f.id}`}
                href={f.url}
                target="_blank"
                rel="noopener noreferrer"
                className={rowCls}
              >
                {inner}
              </motion.a>
            );
          })}
        </div>

        <motion.div {...reveal} className="mt-16">
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-ink3 mb-5">{t.fiction.othersTitle}</p>
          <div className="flex flex-wrap gap-2.5">
            {otherCredits.map((c) => (
              <span
                key={c}
                className="border border-line rounded-full px-4 py-1.5 text-xs sm:text-sm text-ink2 hover:border-brass hover:text-brass transition-colors duration-300 cursor-default"
              >
                {c}
              </span>
            ))}
          </div>
        </motion.div>
      </div>

      <AnimatePresence>
        {trailer && (
          <motion.div
            data-testid="film-trailer-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8"
            onClick={() => setTrailer(null)}
          >
            <motion.div
              initial={{ scale: 0.94, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="w-full max-w-4xl"
              onClick={(e) => e.stopPropagation()}
            >
              <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/60 mb-3">{trailer.title}</p>
              <div className="aspect-video w-full rounded-sm overflow-hidden shadow-2xl bg-black">
                {trailer.youtube ? (
                  <iframe
                    className="w-full h-full"
                    src={`https://www.youtube.com/embed/${trailer.youtube}?autoplay=1&rel=0`}
                    title={`${trailer.title} — trailer`}
                    referrerPolicy="strict-origin-when-cross-origin"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                ) : (
                  <video
                    className="w-full h-full"
                    src={trailer.video}
                    controls
                    autoPlay
                    playsInline
                  />
                )}
              </div>
              {trailer.youtube && (
                <a
                  data-testid="film-trailer-youtube-link"
                  href={trailer.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 mt-4 text-sm text-white/70 hover:text-white transition-colors"
                >
                  {t.fiction.openYoutube}
                  <ArrowUpRight size={14} />
                </a>
              )}
            </motion.div>
            <button
              data-testid="film-trailer-close"
              onClick={() => setTrailer(null)}
              aria-label="Close"
              className="absolute top-6 right-6 w-11 h-11 rounded-full border border-white/30 text-white flex items-center justify-center hover:bg-white/10 transition-colors"
            >
              <X size={18} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
